// Security engine (Domain 06): access control lists, management-plane access
// (console / VTY lines, enable secret, local users, SSH and Telnet), AAA with
// RADIUS and TACACS+ servers, and switch port security.
//
// Everything is plain JSON on the device objects (persisted with the lab):
//   d.acls[name]   = [ ACE ]            (numbered and named ACLs share this map)
//   d.aclType[name]= 'standard'|'extended'  (named ACLs; numbered ones derive it)
//   d.mgmt         = management-plane configuration (see mgmtOf below)
//   iface.accessGroup = { in: name|null, out: name|null }   (routers)
//   iface.psec        = port security (switch access ports)
//   iface.errDisabled = 'psecure-violation' | null
//   host.aaa          = an AAA server (RADIUS / TACACS+) on a Windows server
//
// Behaviour derives from that state only. Checks run on clones; learner traffic
// mutates the live network (ACL hit counters, port-security learning) exactly as
// the forwarding engine does for ARP and MAC tables.
//
// Modelling simplifications (also stated in the Domain 06 missions):
//   * all VTY lines share one configuration (line vty 0 4 and 5 15 behave alike);
//   * a remote SSH/Telnet session verifies the connection and the login, then
//     closes — commands are not executed remotely (use the device console tab);
//   * RADIUS/TACACS+ exchanges are evaluated as one request/response: a reachable
//     server with the right shared key answers accept/reject; anything else is a
//     timeout and the next method in the list is tried;
//   * ACL `log` writes one message per new flow (real IOS rate-limits by time);
//   * hashes shown for `secret` are lab placeholders in the correct format, not
//     real MD5-crypt/scrypt output. Type 7 encoding is the real (weak) algorithm.

import { ipToInt, wildcardMatch, macCisco, isIp } from './addr.js';

// ------------------------------------------------------------------ ACLs
export const TCP_PORTS = { 'ftp-data': 20, ftp: 21, telnet: 23, smtp: 25, tacacs: 49, domain: 53, www: 80, pop3: 110, bgp: 179 };
export const UDP_PORTS = { domain: 53, bootps: 67, bootpc: 68, tftp: 69, ntp: 123, snmp: 161, snmptrap: 162, isakmp: 500, syslog: 514 };
export const ICMP_TYPES = { echo: 8, 'echo-reply': 0, unreachable: 3, 'time-exceeded': 11 };
const portName = (proto, n) => {
  const t = proto === 'udp' ? UDP_PORTS : TCP_PORTS;
  return Object.keys(t).find((k) => t[k] === n) || String(n);
};
/** Port token → number (numeric or IOS keyword for the protocol), or null. */
export function portOf(proto, tok) {
  if (/^\d+$/.test(tok)) { const n = +tok; return n >= 0 && n <= 65535 ? n : null; }
  const t = proto === 'udp' ? UDP_PORTS : TCP_PORTS;
  return t[String(tok).toLowerCase()] ?? null;
}

export const isNumberedStd = (n) => /^\d+$/.test(n) && ((+n >= 1 && +n <= 99) || (+n >= 1300 && +n <= 1999));
export const isNumberedExt = (n) => /^\d+$/.test(n) && ((+n >= 100 && +n <= 199) || (+n >= 2000 && +n <= 2699));
/** 'standard' | 'extended' | null (unknown named list). */
export function aclKind(d, name) {
  if (isNumberedStd(name)) return 'standard';
  if (isNumberedExt(name)) return 'extended';
  return d.aclType?.[name] || (d.acls?.[name] ? 'standard' : null);
}
/** Entries with sequence numbers (older labs stored plain arrays without them). */
export function aclEntries(d, name) {
  const a = d.acls?.[name];
  if (!a) return null;
  let last = 0;
  for (const e of a) { if (!e.seq) e.seq = last + 10; last = e.seq; }
  return a;
}
const addrText = (a) => (a.wild === '255.255.255.255' && a.base === '0.0.0.0' ? 'any' : a.wild === '0.0.0.0' ? `host ${a.base}` : `${a.base} ${a.wild}`);
const portText = (proto, p) => (!p ? '' : p.op === 'range' ? ` range ${portName(proto, p.a)} ${portName(proto, p.b)}` : ` ${p.op} ${portName(proto, p.a)}`);
/** IOS text of one entry (as in show running-config, without the action prefix). */
export function aceText(e) {
  if (e.remark != null) return `remark ${e.remark}`;
  if (e.kind === 'ext') {
    let t = `${e.action} ${e.proto} ${addrText(e.src)}${portText(e.proto, e.sport)} ${addrText(e.dst)}${portText(e.proto, e.dport)}`;
    if (e.icmpType) t += ` ${e.icmpType}`;
    if (e.established) t += ' established';
    if (e.log) t += ' log';
    return t;
  }
  // standard (legacy entries keep their stored text)
  const base = e.text || addrText({ base: e.base, wild: e.wild });
  return `${e.action} ${base}${e.log ? ' log' : ''}`;
}
/** One line of `show access-lists` for an entry. */
export function aceShow(e) {
  if (e.remark != null) return null;
  let body;
  if (e.kind === 'ext') body = aceText(e);
  else {
    const a = e.wild === '255.255.255.255' && e.base === '0.0.0.0' ? 'any' : e.wild === '0.0.0.0' || !e.wild ? e.base : `${e.base}, wildcard bits ${e.wild}`;
    body = `${e.action.padEnd(6)} ${a}${e.log ? ' log' : ''}`;
  }
  return `    ${e.seq} ${body}${e.hits ? ` (${e.hits} match${e.hits === 1 ? '' : 'es'})` : ''}`;
}
const inAddr = (ip, a) => !!ip && wildcardMatch(ip, a.base, a.wild);
const portOk = (p, n) => {
  if (!p) return true;
  if (n == null) return false;
  if (p.op === 'eq') return n === p.a;
  if (p.op === 'neq') return n !== p.a;
  if (p.op === 'gt') return n > p.a;
  if (p.op === 'lt') return n < p.a;
  return n >= p.a && n <= p.b;
};
/** Does one ACE match a packet { src, dst, proto, sport, dport, icmp, reply }? */
export function aceMatches(e, pkt, kind) {
  if (e.remark != null) return false;
  if (kind === 'standard' || e.kind !== 'ext') return inAddr(pkt.src, { base: e.base, wild: e.wild ?? '0.0.0.0' });
  if (e.proto !== 'ip' && e.proto !== pkt.proto) return false;
  if (!inAddr(pkt.src, e.src) || !inAddr(pkt.dst, e.dst)) return false;
  if (e.proto === 'tcp' || e.proto === 'udp') {
    if (!portOk(e.sport, pkt.sport) || !portOk(e.dport, pkt.dport)) return false;
    if (e.established && !pkt.reply) return false;
  }
  if (e.proto === 'icmp' && e.icmpType && (pkt.icmp || 'echo') !== e.icmpType) return false;
  return true;
}
/**
 * Evaluate an ACL against a packet. A list that does not exist permits everything
 * (IOS behaviour for an access-group pointing at an undefined list).
 * Returns { permit, entry, implicit, missing }.
 */
export function evalAcl(d, name, pkt, { count = false } = {}) {
  const list = aclEntries(d, name);
  if (!list) return { permit: true, entry: null, missing: true };
  const kind = aclKind(d, name);
  for (const e of list) {
    if (!aceMatches(e, pkt, kind)) continue;
    if (count) e.hits = (e.hits || 0) + 1;
    return { permit: e.action === 'permit', entry: e };
  }
  return { permit: false, entry: null, implicit: true };
}

/**
 * Interface packet filter used by the forwarding engine. `dir` 'in' | 'out'.
 * Returns true when the packet may pass. Updates hit counters and ACL `log`.
 */
export function aclFilter(net, devId, ifc, dir, pkt, log) {
  const d = net.devices[devId];
  const name = d.interfaces?.[ifc]?.accessGroup?.[dir];
  if (!name) return true;
  const r = evalAcl(d, name, pkt, { count: true });
  if (r.entry?.log && log) {
    const key = `${name}|${r.entry.seq}|${pkt.src}|${pkt.dst}|${pkt.proto}|${pkt.dport || ''}`;
    d.aclLogged ||= [];
    if (!d.aclLogged.includes(key)) {
      d.aclLogged.push(key);
      if (d.aclLogged.length > 60) d.aclLogged.shift();
      const ports = pkt.proto === 'tcp' || pkt.proto === 'udp';
      log(net, devId, ports
        ? `%SEC-6-IPACCESSLOGP: list ${name} ${r.permit ? 'permitted' : 'denied'} ${pkt.proto} ${pkt.src}(${pkt.sport || 0}) -> ${pkt.dst}(${pkt.dport || 0}), 1 packet`
        : `%SEC-6-IPACCESSLOGDP: list ${name} ${r.permit ? 'permitted' : 'denied'} icmp ${pkt.src} -> ${pkt.dst} (${ICMP_TYPES[pkt.icmp || 'echo']}/0), 1 packet`);
    }
  }
  return r.permit;
}

// ------------------------------------------------------------------ passwords
const XLAT = 'dsfd;kfoA,.iyewrkldJKDHSUBsgvca69834ncxv9873254k;fg87';
/** Cisco type 7 (reversible, weak) — the real algorithm, so learners can see why it is weak. */
export function type7(pw, salt = 2) {
  let out = String(salt).padStart(2, '0');
  for (let i = 0; i < pw.length; i++) out += ((pw.charCodeAt(i) ^ XLAT.charCodeAt((salt + i) % XLAT.length)) & 0xff).toString(16).toUpperCase().padStart(2, '0');
  return out;
}
export function type7Decode(enc) {
  const salt = +enc.slice(0, 2);
  let out = '';
  for (let i = 2; i < enc.length; i += 2) out += String.fromCharCode(parseInt(enc.slice(i, i + 2), 16) ^ XLAT.charCodeAt((salt + (i - 2) / 2) % XLAT.length));
  return out;
}
const B64 = './0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
function digest(s, n) {
  let h1 = 0x811c9dc5, h2 = 0x1b873593, out = '';
  for (let k = 0; out.length < n; k++) {
    for (let i = 0; i < s.length; i++) { h1 = Math.imul(h1 ^ s.charCodeAt(i), 16777619) >>> 0; h2 = Math.imul(h2 + s.charCodeAt(i) + k, 2246822519) >>> 0; }
    out += B64[(h1 ^ h2) & 63] + B64[(h1 >>> 6) & 63] + B64[(h2 >>> 12) & 63];
  }
  return out.slice(0, n);
}
/** Display form of a stored secret: type 5 ($1$salt$hash) or type 9 ($9$...). Lab placeholder digest. */
/**
 * Does a typed password match a stored entry? Entries typed in the lab keep the
 * clear value (pw); entries loaded from a configuration file (enable secret 5 <hash>,
 * e.g. restored from a TFTP backup) keep only the hash, compared like IOS does: by
 * hashing the input.
 */
export function pwMatches(e, input) {
  if (!e) return false;
  if (e.pw != null) return e.pw === input;
  if (e.hash) return secretText(input, e.type) === `${e.type} ${e.hash}`;
  return false;
}
/** Running-config text of a secret entry (hash only). */
export const secretOf = (e) => (e.pw != null ? secretText(e.pw, e.type) : `${e.type} ${e.hash}`);
export const secretText = (pw, type = 5, salt = 'rzLb') => (type === 9 ? `9 $9$${digest(`s${pw}`, 14)}$${digest(pw + salt, 43)}` : `5 $1$${salt}$${digest(pw + salt, 22)}`);

// ------------------------------------------------------------------ management plane
const DEF_LINE = (kind) => ({ password: null, login: kind === 'vty' ? 'line' : 'none', authList: null, execTimeout: null, transport: ['all'], accessClass: null, logSync: false });
/** Effective management configuration (read-only defaults when nothing was configured). */
export function mgmtOf(d) {
  return d.mgmt || {
    enableSecret: null, enablePassword: null, encrypt: false, users: {}, domainName: null, rsa: null, ssh: { version: null, timeout: 120, retries: 3 },
    banner: null, minLength: 0, loginLog: { failure: false, success: false }, httpServer: false,
    lines: { con: DEF_LINE('con'), vty: DEF_LINE('vty') }, aaa: null, defaulted: true,
  };
}
/** Mutable management configuration (created on first security command). */
export function ensureMgmt(d) {
  if (!d.mgmt) { d.mgmt = mgmtOf(d); delete d.mgmt.defaulted; }
  return d.mgmt;
}
export const aaaOf = (d) => (ensureMgmt(d).aaa ||= { newModel: false, authLogin: {}, acctExec: null, servers: { radius: {}, tacacs: {} }, stats: {} });
export const sshEnabled = (d) => !!mgmtOf(d).rsa;

/** Is a management service listening on (router|switch) d for proto/port? */
export function mgmtListens(d, proto, port) {
  if (proto !== 'tcp') return false;
  const m = mgmtOf(d);
  const t = m.lines.vty.transport;
  const allows = (p) => t.includes('all') || t.includes(p);
  if (port === 22) return sshEnabled(d) && allows('ssh');
  if (port === 23) return allows('telnet');
  if (port === 80) return !!m.httpServer;
  return false;
}

/** The server host that owns an IP, if it runs an AAA service. */
function aaaHost(net, ip) {
  return Object.values(net.devices).find((x) => (x.kind === 'server' || x.kind === 'pc') && x.aaa && (x.ipcfg?.ip === ip || x.lease?.ip === ip));
}

/**
 * One AAA request from device `devId` to a configured server.
 * deps: { reach(net, fromId, ip, opts) → { ok, src } } (injected by sim.js to avoid a cycle).
 * Returns { result: 'accept'|'reject'|'timeout', priv }.
 */
export function aaaRequest(net, devId, proto, srv, user, pass, deps) {
  const port = proto === 'radius' ? srv.authPort || 1812 : 49;
  const r = deps.reach(net, devId, srv.ip, { proto: proto === 'radius' ? 'udp' : 'tcp', port });
  if (!r.ok) return { result: 'timeout', why: 'unreachable' };
  const host = aaaHost(net, srv.ip);
  const svc = host?.aaa?.[proto];
  if (!svc || svc.enabled === false) return { result: 'timeout', why: 'no-service' };
  const client = svc.clients?.[r.src];
  if (!client) return { result: 'timeout', why: 'unknown-client' };          // unknown NAS: request dropped
  if (client.key !== srv.key) return { result: 'timeout', why: 'key-mismatch' }; // wrong shared secret: dropped
  const u = host.aaa.users?.[user];
  const ok = !!u && u.password === pass && (!u.allow || u.allow.includes(proto));
  return { result: ok ? 'accept' : 'reject', priv: ok ? u.priv ?? 1 : null, host: host.id };
}

const methodList = (m, lineCfg) => {
  const a = m.aaa;
  const name = lineCfg.authList || 'default';
  return a?.authLogin?.[name] || (name === 'default' ? null : undefined);
};
/**
 * Authenticate a login on a console or VTY line.
 * opts: { line: 'con'|'vty', user, pass, src, record } ; deps: { reach }.
 * Returns { ok, priv, reason, method, needUser, detail[] }.
 * reason: 'no-password-set' | 'bad' | 'no-method' | 'all-failed'
 */
export function authenticate(net, devId, opts, deps) {
  const d = net.devices[devId];
  const m = mgmtOf(d);
  const L = m.lines[opts.line];
  const detail = [];
  const local = () => {
    const u = m.users[opts.user];
    return u && pwMatches(u, opts.pass) ? { ok: true, priv: u.priv ?? 1, method: 'local' } : { ok: false, reason: 'bad', method: 'local' };
  };
  if (m.aaa?.newModel) {
    const list = methodList(m, L);
    if (list === undefined) return { ok: false, reason: 'no-method', detail: [`% Authentication list ${L.authList} not defined`] };
    const methods = list || ['local']; // aaa new-model without a default list: the local database
    for (const meth of methods) {
      if (meth === 'local' || meth === 'local-case') {
        // IOS: "local" is authoritative only when the database has users; with none it errors and moves on.
        if (!Object.keys(m.users).length) { detail.push('local: no users defined'); continue; }
        return { ...local(), detail };
      }
      if (meth === 'none') return { ok: true, priv: 1, method: 'none', detail };
      if (meth === 'line') { if (!L.password) { detail.push('line: no password'); continue; } return { ok: L.password === opts.pass, priv: 1, reason: 'bad', method: 'line', detail }; }
      if (meth === 'enable') { const e = m.enableSecret || m.enablePassword; if (!e) { detail.push('enable: no password'); continue; } return { ok: pwMatches(e, opts.pass), priv: 1, reason: 'bad', method: 'enable', detail }; }
      const proto = meth === 'group radius' ? 'radius' : meth === 'group tacacs+' ? 'tacacs' : null;
      if (!proto) continue;
      const servers = Object.entries(m.aaa.servers[proto] || {});
      for (const [name, srv] of servers) {
        const r = aaaRequest(net, devId, proto, srv, opts.user, opts.pass, deps);
        detail.push(`${proto} ${name}: ${r.result}${r.why ? ` (${r.why})` : ''}`);
        if (opts.record) recordAaa(net, devId, proto, name, srv, r, opts);
        if (r.result === 'timeout') continue;
        return { ok: r.result === 'accept', priv: r.priv ?? 1, reason: 'bad', method: meth, server: name, detail };
      }
    }
    return { ok: false, reason: 'all-failed', detail };
  }
  if (L.login === 'none') return { ok: true, priv: 1, method: 'none', noLogin: true, detail };
  if (L.login === 'local') return { ...local(), detail };
  if (!L.password) return { ok: false, reason: 'no-password-set', detail };
  return { ok: L.password === opts.pass, priv: 1, reason: 'bad', method: 'line', detail };
}
function recordAaa(net, devId, proto, name, srv, r, opts) {
  const d = net.devices[devId];
  const st = (aaaOf(d).stats[`${proto}|${name}`] ||= { requests: 0, accept: 0, reject: 0, timeouts: 0 });
  st.requests++;
  if (r.result === 'accept') st.accept++; else if (r.result === 'reject') st.reject++; else st.timeouts++;
  if (r.result !== 'timeout' && r.host) {
    const h = net.devices[r.host];
    h.aaa.log ||= [];
    h.aaa.log.push({ n: h.aaa.log.length + 1, proto, client: d.hostname, user: opts.user, src: opts.src || 'console', result: proto === 'radius' ? (r.result === 'accept' ? 'Access-Accept' : 'Access-Reject') : (r.result === 'accept' ? 'Authentication PASS' : 'Authentication FAIL'), type: 'auth' });
    if (h.aaa.log.length > 60) h.aaa.log.shift();
  }
}
/** Login method the line would ask for: 'none' | 'password' | 'username' */
export function loginPrompt(d, line) {
  const m = mgmtOf(d);
  const L = m.lines[line];
  if (m.aaa?.newModel) {
    const list = methodList(m, L) || ['local'];
    return list[0] === 'none' ? 'none' : list.every((x) => x === 'line' || x === 'enable') ? 'password' : 'username';
  }
  return L.login === 'none' ? 'none' : L.login === 'local' ? 'username' : 'password';
}
/** Accounting record for a started EXEC session (aaa accounting exec ... start-stop). */
export function accountExec(net, devId, user, src, deps) {
  const d = net.devices[devId];
  const a = mgmtOf(d).aaa;
  if (!a?.newModel || !a.acctExec) return;
  const proto = a.acctExec === 'group tacacs+' ? 'tacacs' : 'radius';
  for (const [, srv] of Object.entries(a.servers[proto] || {})) {
    const r = deps.reach(net, devId, srv.ip, { proto: proto === 'radius' ? 'udp' : 'tcp', port: proto === 'radius' ? srv.acctPort || 1813 : 49 });
    const host = r.ok ? aaaHost(net, srv.ip) : null;
    const c = host?.aaa?.[proto]?.clients?.[r.src];
    if (!c || c.key !== srv.key) continue;
    host.aaa.log ||= [];
    host.aaa.log.push({ n: host.aaa.log.length + 1, proto, client: d.hostname, user, src, result: proto === 'radius' ? 'Accounting-Request (Start)' : 'Accounting START (exec)', type: 'acct' });
    return;
  }
}

// ------------------------------------------------------------------ port security
export const PSEC_DEFAULT = () => ({ enabled: false, max: 1, violation: 'shutdown', sticky: false, macs: [], count: 0, last: null });
export const psecOf = (i) => i.psec || null;
/**
 * A frame from `mac` enters switch access port `port` (VLAN `vlan`).
 * Returns { pass, logs:[...] }. Mutates the port (learning, violation, err-disable).
 */
export function psecIngress(sw, port, mac, vlan) {
  const i = sw.interfaces[port];
  const p = i?.psec;
  if (!p?.enabled || i.mode === 'trunk') return { pass: true, logs: [] };
  const m = macCisco(mac);
  p.last = `${m}:${vlan}`;
  if (p.macs.some((x) => x.mac === m)) return { pass: true, logs: [] };
  if (p.macs.length < p.max) {
    p.macs.push({ mac: m, type: p.sticky ? 'sticky' : 'dynamic', vlan });
    return { pass: true, logs: [] };
  }
  // violation
  const short = shortPort(port);
  const vlog = `%PORT_SECURITY-2-PSECURE_VIOLATION: Security violation occurred, caused by MAC address ${m} on port ${port}.`;
  if (p.violation === 'protect') return { pass: false, logs: [] };
  p.count++;
  if (p.violation === 'restrict') return { pass: false, logs: [vlog] };
  i.errDisabled = 'psecure-violation';
  p.macs = p.macs.filter((x) => x.type !== 'dynamic');
  return {
    pass: false, shutdown: true,
    logs: [`%PM-4-ERR_DISABLE: psecure-violation error detected on ${short}, putting ${short} in err-disable state`, vlog,
      `%LINEPROTO-5-UPDOWN: Line protocol on Interface ${port}, changed state to down`, `%LINK-3-UPDOWN: Interface ${port}, changed state to down`],
  };
}
const shortPort = (n) => n.replace('FastEthernet', 'Fa').replace('GigabitEthernet', 'Gi');
export const isMac = (t) => /^[0-9a-f]{4}\.[0-9a-f]{4}\.[0-9a-f]{4}$/i.test(t);

export { isIp, ipToInt };
